import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shell } from '../components/Shell';
import { useAuth } from '../lib/auth';
import { getActiveTenant, getTenant, clearActiveTenant } from '../lib/tenant';
import { CMP_DOMAIN } from '../lib/firebase';

export function DashboardPage() {
  const { user, loading, signOut } = useAuth();
  const tenantId = getActiveTenant();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<Record<string, unknown> | null>(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [showAddDomain, setShowAddDomain] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    (async () => {
      const t = await getTenant(tenantId);
      setTenant(t);
      setTenantLoading(false);
    })();
  }, [tenantId]);

  if (loading || tenantLoading) {
    return (
      <Shell centerLabel="Dashboard">
        <div className="px-6 md:px-14 py-20 meta">Loading…</div>
      </Shell>
    );
  }

  if (!user) {
    return (
      <Shell centerLabel="Dashboard">
        <section className="border-b" style={{ borderColor: 'var(--rule)' }}>
          <div className="max-w-[640px] mx-auto px-6 md:px-14 py-20">
            <div className="meta mb-3" style={{ color: 'var(--flag)' }}>Not signed in</div>
            <Link to="/signup" className="btn inline-flex">
              <span>Send me a sign-in link</span> <span className="slash">/</span>
            </Link>
          </div>
        </section>
      </Shell>
    );
  }

  if (!tenantId || !tenant) {
    return (
      <Shell centerLabel="Dashboard">
        <section className="border-b" style={{ borderColor: 'var(--rule)' }}>
          <div className="max-w-[640px] mx-auto px-6 md:px-14 py-20">
            <div className="meta mb-3" style={{ color: 'var(--flag)' }}>No workspace</div>
            <p className="text-[14px] leading-[1.55] mb-6" style={{ color: 'rgba(13,11,31,0.75)' }}>
              You haven&apos;t completed onboarding.
            </p>
            <Link to="/onboarding/site" className="btn inline-flex">
              <span>Start onboarding</span> <span className="slash">/</span>
            </Link>
          </div>
        </section>
      </Shell>
    );
  }

  const primaryDomain = (tenant.primaryDomain as string) || (tenant.name as string) || tenantId;

  return (
    <Shell centerLabel={`Dashboard · ${tenantId}`} rightLinks={false}>
      <section className="border-b" style={{ borderColor: 'var(--rule)' }}>
        <div className="max-w-[1200px] mx-auto px-6 md:px-14 py-12">
          <div className="flex justify-between items-baseline flex-wrap gap-3 mb-8">
            <div>
              <span className="meta">Workspace</span>
              <h1 className="font-medium mt-2" style={{ fontSize: 'clamp(28px, 3.4vw, 44px)', lineHeight: 1.04, letterSpacing: '-0.03em' }}>
                Welcome, <span className="grad-text">{user.email}</span>
              </h1>
              <p className="text-[14px] mt-2" style={{ color: 'rgba(13,11,31,0.7)' }}>
                Banner live on <strong>{primaryDomain}</strong>. Add more domains, tweak the banner, or watch acceptance stats below.
              </p>
            </div>
            <button
              className="btn btn-ghost"
              onClick={async () => {
                clearActiveTenant();
                await signOut();
                navigate('/');
              }}
            >
              Sign out
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
            <Stat label="Banner shows · 7d" value="-" hint="Stats appear once the CMP starts receiving consent events." />
            <Stat label="Acceptance rate" value="-" hint="Aggregate accept / reject ratio." />
            <Stat label="Cookies discovered" value="-" hint="From weekly automated rescans." />
          </div>

          <div className="flex justify-between items-baseline mb-3">
            <h2 className="font-medium" style={{ fontSize: 22, letterSpacing: '-0.02em' }}>
              Domains{' '}
              <span style={{ fontSize: 12, color: 'var(--muted-ink)', fontWeight: 400, marginLeft: 8 }}>
                1 of 1 used · Free plan
              </span>
            </h2>
            {!showAddDomain && (
              <button className="btn" onClick={() => setShowAddDomain(true)}>
                + Add domain
              </button>
            )}
          </div>

          {showAddDomain && (
            <div
              style={{
                background: 'white',
                border: '1px solid var(--ink)',
                borderRadius: 4,
                padding: '22px 24px',
                marginBottom: 14,
              }}
            >
              <span className="meta mb-2 block">New domain</span>
              <div className="flex gap-3 flex-wrap items-start">
                <div className="flex-1 min-w-[260px]">
                  <label className="field" style={{ marginBottom: 0 }}>
                    <span className="field-label">Domain</span>
                    <input type="text" placeholder="another-site.com" />
                    <span className="field-hint">We&apos;ll generate a snippet you can install in seconds.</span>
                  </label>
                </div>
                <div className="flex flex-col gap-2" style={{ paddingTop: 24 }}>
                  <button
                    className="btn"
                    onClick={() => {
                      alert('Adding a second domain requires upgrading to Solo (€19/mo, 5 domains) or Agency (€79/mo, 25 domains).');
                      setShowAddDomain(false);
                    }}
                  >
                    Create &amp; get snippet <span className="slash">/</span>
                  </button>
                  <button className="btn btn-ghost" onClick={() => setShowAddDomain(false)}>
                    Cancel
                  </button>
                </div>
              </div>
              <p style={{ marginTop: 14, fontSize: 12, color: 'var(--muted-ink)', lineHeight: 1.55 }}>
                You&apos;re on the Free plan, which allows 1 domain. Adding a second domain will prompt you to upgrade.
              </p>
            </div>
          )}

          <div style={{ background: 'white', border: '1px solid var(--rule)', borderRadius: 4 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '12px 16px',
                fontSize: 13.5,
              }}
            >
              <span style={{ flex: 1, fontFamily: 'IBM Plex Mono, monospace' }}>{primaryDomain}</span>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: 99,
                  fontSize: 10,
                  fontFamily: 'IBM Plex Mono, monospace',
                  letterSpacing: '1.4px',
                  textTransform: 'uppercase',
                  border: '1px solid var(--live)',
                  color: 'var(--live)',
                }}
              >
                Live
              </span>
            </div>
          </div>

          <h2 className="font-medium mt-10 mb-3" style={{ fontSize: 22, letterSpacing: '-0.02em' }}>
            Quick links
          </h2>
          <ul style={{ fontSize: 14, lineHeight: 1.8, margin: '8px 0 0 18px' }}>
            <li>
              <a className="underline" style={{ color: 'var(--ink)' }} href={`${CMP_DOMAIN}/v1/cmp.js?tenant=${tenantId}`} target="_blank" rel="noreferrer">
                Verify your CMP endpoint
              </a>
            </li>
            <li>
              <Link className="underline" style={{ color: 'var(--ink)' }} to="/install">
                View install snippet
              </Link>
            </li>
            <li>
              <Link className="underline" style={{ color: 'var(--ink)' }} to="/pricing">
                See pricing plans
              </Link>
            </li>
          </ul>

          <div
            style={{
              marginTop: 30,
              padding: '18px 22px',
              background: 'var(--ink)',
              color: 'var(--paper)',
              borderRadius: 4,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 14,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <span className="meta" style={{ color: 'rgba(240,238,233,0.55)' }}>
                You&apos;re on Free · 1 domain · WeDPO attribution shown
              </span>
              <div style={{ fontSize: 16, fontWeight: 500, marginTop: 4 }}>
                Unlock white-label + more domains
              </div>
            </div>
            <Link className="btn" style={{ background: 'var(--paper)', color: 'var(--ink)' }} to="/pricing">
              Upgrade to Pro · €19/mo
            </Link>
          </div>
        </div>
      </section>
    </Shell>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div
      style={{
        background: 'white',
        border: '1px solid var(--rule)',
        padding: '18px 20px',
        borderRadius: 4,
      }}
    >
      <span className="meta" style={{ display: 'block', marginBottom: 8 }}>{label}</span>
      <div style={{ fontSize: 28, fontWeight: 500, letterSpacing: '-0.025em' }}>{value}</div>
      <span style={{ display: 'block', marginTop: 4, fontSize: 11.5, color: 'var(--muted-ink)' }}>{hint}</span>
    </div>
  );
}
